/**
 * Antwort-Fenster unten im Mail-Fenster (Mockup V4, docs/KONZEPT-GESPRAECH-MAILFENSTER.md Punkt 5):
 * Kopf „Antwort an <Name>“ mit „Senden“, Zeilen An / Von / Betreff, Textfeld, darunter KI-Entwurf,
 * ☑ Kopie an mich (<Adresse>) und ☑ Zitat anhaengen.
 *
 * Senden = die bestehende Route `POST /api/events/:id/reply`, erweitert um `subject`, `kopieAnMich`,
 * `zitatAnhaengen`, `antwortAufEmailId`. „KI-Entwurf“ fuellt NUR das Textfeld
 * (`POST .../reply/entwurf`), sendet nichts. Die Haken kommen aus der gespeicherten Praeferenz
 * (`GET /api/events/gespraech-praeferenz`) und werden bei jeder Aenderung gespeichert (PUT).
 * Ein Anhang-Knopf fehlt bewusst: die Route kann heute keine Anhaenge versenden.
 */
import { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Icon, Input, Textarea } from "../components";
import { apiMutate, useApi } from "../../hooks/useApi";
import type { ApiError } from "../../lib/api";
import { adresseVon, antwortBetreff } from "../../lib/gespraech-mailfenster";

interface Praeferenz {
  kopieAnMich: boolean;
  zitatAnhaengen: boolean;
  eigeneEmail?: string | null;
}

export interface AntwortZustand {
  text: string;
  setText: (t: string) => void;
  betreff: string;
  setBetreff: (b: string) => void;
  kopieAnMich: boolean;
  zitatAnhaengen: boolean;
  eigeneEmail: string | null;
  setzeKopieAnMich: (v: boolean) => void;
  setzeZitatAnhaengen: (v: boolean) => void;
  laeuft: boolean;
  fehler: string | null;
  hinweis: string | null;
  entwurfLaeuft: boolean;
  entwurfFehler: string | null;
  senden: () => Promise<void>;
  entwurfLaden: () => Promise<void>;
  fokussieren: () => void;
  feldRef: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Zustand des Antwortfensters. `antwortAufId` = Id der beantworteten (echten) Gast-Nachricht oder
 * null (dann kein Zitat); `standardBetreff` = Betreff der beantworteten Nachricht.
 */
export function useGespraechAntwort({
  eventId,
  antwortAufId,
  standardBetreff,
  onGesendet,
}: {
  eventId: string;
  antwortAufId: string | null;
  standardBetreff: string | null;
  onGesendet?: () => void;
}): AntwortZustand {
  const praef = useApi<Praeferenz>("/api/events/gespraech-praeferenz");
  const [text, setText] = useState("");
  const [betreffEigen, setBetreffEigen] = useState<string | null>(null);
  const [kopieAnMich, setKopieAnMich] = useState(false);
  const [zitatAnhaengen, setZitatAnhaengen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [entwurfLaeuft, setEntwurfLaeuft] = useState(false);
  const [entwurfFehler, setEntwurfFehler] = useState<string | null>(null);
  const feldRef = useRef<HTMLTextAreaElement | null>(null);
  // Die geladene Praeferenz uebernehmen, solange der Nutzer noch nichts selbst umgelegt hat.
  const beruehrt = useRef(false);
  const geladen = praef.data && !Array.isArray(praef.data) ? praef.data : null;
  useEffect(() => {
    if (!geladen || beruehrt.current) return;
    setKopieAnMich(Boolean(geladen.kopieAnMich));
    setZitatAnhaengen(Boolean(geladen.zitatAnhaengen));
  }, [geladen]);

  // Betreff: solange der Nutzer ihn nicht selbst aendert, folgt er der beantworteten Nachricht.
  const betreff = betreffEigen ?? antwortBetreff(standardBetreff);
  useEffect(() => setBetreffEigen(null), [antwortAufId]);

  function speichern(kopie: boolean, zitat: boolean) {
    // Reine Bequemlichkeit: ein Fehler beim Merken darf das Antworten nie stoeren.
    void apiMutate(`/api/events/gespraech-praeferenz`, "PUT", { kopieAnMich: kopie, zitatAnhaengen: zitat }).catch(() => undefined);
  }
  function setzeKopieAnMich(v: boolean) {
    beruehrt.current = true;
    setKopieAnMich(v);
    speichern(v, zitatAnhaengen);
  }
  function setzeZitatAnhaengen(v: boolean) {
    beruehrt.current = true;
    setZitatAnhaengen(v);
    speichern(kopieAnMich, v);
  }

  async function senden() {
    const t = text.trim();
    if (!t) return;
    setLaeuft(true);
    setFehler(null);
    setHinweis(null);
    try {
      const r = await apiMutate<{ kopie?: { gesendet: boolean; an?: string; grund?: string } } | undefined>(`/api/events/${eventId}/reply`, "POST", {
        channel: "email",
        text: t,
        subject: betreff.trim() || undefined,
        kopieAnMich,
        zitatAnhaengen,
        ...(antwortAufId ? { antwortAufEmailId: antwortAufId } : {}),
      });
      if (kopieAnMich && r?.kopie && !r.kopie.gesendet && r.kopie.grund !== "gleiche_adresse") {
        setHinweis("Die Antwort ist raus. Die Kopie an dich konnte nicht gesendet werden.");
      }
      setText("");
      setBetreffEigen(null);
      onGesendet?.();
    } catch (err) {
      const apiErr = err as ApiError;
      setFehler(
        apiErr?.code === "no_recipient"
          ? "Der Kunde hat keine E-Mail-Adresse hinterlegt — die Antwort wurde nicht versendet."
          : apiErr?.code === "mail_not_configured"
            ? "Mail-Versand ist für diesen Mandanten nicht eingerichtet."
            : apiErr?.code === "send_failed"
              ? `Der Versand ist fehlgeschlagen: ${apiErr?.message ?? "unbekannter Fehler"}`
              : err instanceof Error
                ? err.message
                : String(err),
      );
    } finally {
      setLaeuft(false);
    }
  }

  /** Fuellt das Antwortfeld mit einem KI-Vorschlag aus dem bisherigen Gespraech — sendet nichts. */
  async function entwurfLaden() {
    setEntwurfLaeuft(true);
    setEntwurfFehler(null);
    try {
      const r = await apiMutate<{ text: string }>(`/api/events/${eventId}/reply/entwurf`, "POST");
      setText(r.text);
    } catch (err) {
      const apiErr = err as ApiError;
      setEntwurfFehler(
        apiErr?.code === "daily_cap_reached"
          ? "Tägliches Kontingent für KI-Entwürfe ist erreicht."
          : apiErr?.code === "ai_not_configured"
            ? "KI-Entwurf ist nicht konfiguriert."
            : err instanceof Error
              ? err.message
              : String(err),
      );
    } finally {
      setEntwurfLaeuft(false);
    }
  }

  return {
    text,
    setText,
    betreff,
    setBetreff: setBetreffEigen,
    kopieAnMich,
    zitatAnhaengen,
    eigeneEmail: geladen?.eigeneEmail ?? null,
    setzeKopieAnMich,
    setzeZitatAnhaengen,
    laeuft,
    fehler,
    hinweis,
    entwurfLaeuft,
    entwurfFehler,
    senden,
    entwurfLaden,
    fokussieren: () => feldRef.current?.focus(),
    feldRef,
  };
}

export function GespraechAntwort({
  z,
  kundenName,
  kundenEmail,
  vonAnzeige,
}: {
  z: AntwortZustand;
  kundenName: string | null;
  kundenEmail: string | null;
  vonAnzeige: string | null;
}) {
  const anrede = kundenName?.trim() || "den Kunden";
  const an = kundenEmail ? adresseVon(kundenEmail) : null;
  return (
    <section data-testid="gespraech-antwort" aria-label={`Antwort an ${anrede}`} className="border-t border-outline-variant/40 bg-surface-container-lowest dark:border-outline/25 dark:bg-white/[0.02]">
      <div className="flex items-center gap-2 border-b border-outline-variant/25 px-3.5 py-2.5 dark:border-outline/15">
        <span className="min-w-0 flex-1 truncate font-body-sm text-[15px] font-bold text-on-background dark:text-inverse-on-surface">Antwort an {anrede}</span>
        <Button
          variant="secondary"
          data-testid="gespraech-antwort-senden"
          disabled={z.laeuft || z.entwurfLaeuft || !z.text.trim()}
          onClick={() => void z.senden()}
          className="shrink-0 !py-1.5 !text-[13px] disabled:opacity-50"
        >
          {z.laeuft ? "Sendet …" : "Senden"}
        </Button>
      </div>
      <div className="flex items-center gap-2 border-b border-outline-variant/25 px-3.5 py-2 font-body-sm text-[14.5px] dark:border-outline/15">
        <span className="w-[62px] shrink-0 text-secondary dark:text-secondary-fixed-dim">An</span>
        <span className="min-w-0 truncate rounded-full bg-slate-100 px-2.5 py-px text-[13.5px] font-semibold text-slate-600 dark:bg-slate-700/40 dark:text-slate-300">
          {kundenName?.trim() || an || "Kunde"}
          {an && kundenName?.trim() ? ` <${an}>` : ""}
        </span>
      </div>
      {vonAnzeige && (
        <div className="flex items-center gap-2 border-b border-outline-variant/25 px-3.5 py-2 font-body-sm text-[14.5px] dark:border-outline/15">
          <span className="w-[62px] shrink-0 text-secondary dark:text-secondary-fixed-dim">Von</span>
          <span className="min-w-0 truncate">{vonAnzeige}</span>
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-outline-variant/25 px-3.5 py-1 font-body-sm text-[14.5px] dark:border-outline/15">
        <label htmlFor="gespraech-antwort-betreff" className="w-[62px] shrink-0 text-secondary dark:text-secondary-fixed-dim">
          Betreff
        </label>
        <Input
          id="gespraech-antwort-betreff"
          data-testid="gespraech-antwort-betreff"
          value={z.betreff}
          maxLength={300}
          onChange={(e) => z.setBetreff(e.target.value)}
          className="!border-0 !bg-transparent !px-0 !shadow-none"
        />
      </div>
      <Textarea
        ref={z.feldRef}
        data-testid="gespraech-antwort-text"
        value={z.text}
        onChange={(e) => z.setText(e.target.value)}
        placeholder={`Antwort an ${anrede} …`}
        aria-label={`Antwort an ${anrede}`}
        rows={6}
        className="!min-h-[150px] !w-full !resize-none !rounded-none !border-0 !bg-transparent !px-3.5 !py-3 !text-[15.5px] !leading-[1.55] !shadow-none"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-outline-variant/25 px-3.5 pb-3 pt-2.5 font-body-sm text-[14px] text-on-surface-variant dark:border-outline/15">
        <button
          type="button"
          data-testid="gespraech-entwurf-laden"
          disabled={z.entwurfLaeuft || z.laeuft}
          onClick={() => void z.entwurfLaden()}
          className="inline-flex items-center gap-1 font-semibold text-primary hover:underline disabled:opacity-50"
        >
          <Icon name="auto_awesome" className="text-[18px]" />
          {z.entwurfLaeuft ? "Entwirft …" : "KI-Entwurf"}
        </button>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <Checkbox data-testid="gespraech-kopie-an-mich" checked={z.kopieAnMich} onChange={(e) => z.setzeKopieAnMich(e.target.checked)} />
          <span>Kopie an mich{z.eigeneEmail ? ` (${z.eigeneEmail})` : ""}</span>
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <Checkbox data-testid="gespraech-zitat-anhaengen" checked={z.zitatAnhaengen} onChange={(e) => z.setzeZitatAnhaengen(e.target.checked)} />
          <span>Zitat anhängen</span>
        </label>
      </div>
      {z.entwurfFehler && (
        <p data-testid="gespraech-entwurf-fehler" className="px-3.5 pb-2 font-body-sm text-[12.5px] text-error">
          {z.entwurfFehler}
        </p>
      )}
      {z.fehler && (
        <p data-testid="gespraech-antwort-fehler" className="px-3.5 pb-2 font-body-sm text-[12.5px] text-error">
          {z.fehler}
        </p>
      )}
      {z.hinweis && (
        <p data-testid="gespraech-antwort-hinweis" className="px-3.5 pb-2 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
          {z.hinweis}
        </p>
      )}
    </section>
  );
}
